import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { FunctionalSelect, SelectItem } from '../components/ui/Select';
import ErrorDialog from '../components/ui/ErrorDialog';
import ListPagination, { LIST_PAGE_SIZE } from '../components/ui/ListPagination';
import { toast } from 'react-hot-toast';
import { api } from '../services/authService';
import { organizationsService } from '../services/organizationsService';
import { ChevronRight, ChevronDown, ChevronUp, Download } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const DEFAULT_SORT_BY_FIELD = 'timestamp';
const DEFAULT_SORT_ORDER = 'desc';

function getTodayDate() {
  const today = new Date();
  const y = today.getFullYear();
  const m = String(today.getMonth() + 1).padStart(2, '0');
  const d = String(today.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatBytes(bytes) {
  const n = Math.round(Number(bytes));
  if (!Number.isFinite(n) || n < 0) return String(bytes);
  if (n < 1024) return `${n.toLocaleString()} B`;

  // Pick the largest unit whose rounded display stays below 1024 (except GB).
  const candidates = [
    { unit: 'KB', divisor: 1024, decimalsUnder10: 1 },
    { unit: 'MB', divisor: 1024 ** 2, decimalsUnder10: 1 },
    { unit: 'GB', divisor: 1024 ** 3, decimalsUnder10: 2 },
  ];

  for (let i = 0; i < candidates.length; i++) {
    const { unit, divisor, decimalsUnder10 } = candidates[i];
    const exact = n / divisor;
    const isLast = i === candidates.length - 1;

    if (exact < 10) {
      const displayNum = Number(exact.toFixed(decimalsUnder10));
      if (!isLast && displayNum >= 1024) continue;
      return `${displayNum.toFixed(decimalsUnder10)} ${unit}`;
    }

    const rounded = Math.round(exact);
    if (!isLast && rounded >= 1024) continue;
    return `${rounded.toLocaleString()} ${unit}`;
  }

  return String(bytes);
}

function formatDurationMs(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n < 0) return String(ms);

  const totalSeconds = n / 1000;
  const secondsDisplay =
    totalSeconds < 10 ? Number(totalSeconds.toFixed(1)) : Math.round(totalSeconds);

  // Choose unit from the rounded display so 59,999 ms → 1.0 min, not 60 s.
  if (secondsDisplay < 60) {
    return `${totalSeconds < 10 ? totalSeconds.toFixed(1) : Math.round(totalSeconds).toLocaleString()} s`;
  }

  const totalMinutes = n / 60_000;
  if (totalMinutes < 10) return `${totalMinutes.toFixed(1)} min`;
  return `${Math.round(totalMinutes).toLocaleString()} min`;
}

function compareUsageRecords(a, b, field, order) {
  const av = a?.[field];
  const bv = b?.[field];
  const aMissing = av == null || av === '';
  const bMissing = bv == null || bv === '';
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;

  let cmp;
  if (field === 'response_duration_ms') {
    cmp = Number(av) - Number(bv);
  } else if (field === 'timestamp') {
    const at = Date.parse(av);
    const bt = Date.parse(bv);
    cmp = Number.isNaN(at) || Number.isNaN(bt) ? String(av).localeCompare(String(bv)) : at - bt;
  } else {
    cmp = String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' });
  }
  return order === 'asc' ? cmp : -cmp;
}

const APIUsage = () => {
  const { user: currentUser } = useAuth();
  const [days, setDays] = useState('1');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Populate Date Range from Time Period (end = today, start = today - N days)
  useEffect(() => {
    const start = new Date();
    start.setDate(start.getDate() - parseInt(days, 10));
    setToDate(getTodayDate());
    const y = start.getFullYear();
    const m = String(start.getMonth() + 1).padStart(2, '0');
    const d = String(start.getDate()).padStart(2, '0');
    setFromDate(`${y}-${m}-${d}`);
  }, [days]);
  const [selectedOrg, setSelectedOrg] = useState('all');
  const [selectedUser, setSelectedUser] = useState('all');
  const [selectedEndpoint, setSelectedEndpoint] = useState('all');
  const [executionIdFilter, setExecutionIdFilter] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [isFastCubeAdmin, setIsFastCubeAdmin] = useState(false);
  const [organizationsLoading, setOrganizationsLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [records, setRecords] = useState(null);
  const [listOffset, setListOffset] = useState(0);
  const [sortByField, setSortByField] = useState(DEFAULT_SORT_BY_FIELD);
  const [sortOrder, setSortOrder] = useState(DEFAULT_SORT_ORDER);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Keep in sync with @api_usage(name=...) in the-cube-admin/backend/app/api
  // and databricks-proxy/service/api (sorted alphabetically).
  const endpoints = [
    'analytics_advertiser_performance_hist',
    'analytics_churn_gp',
    'analytics_churn_gp_advertiser',
    'analytics_churn_gp_offers',
    'analytics_daily_conversion_rate',
    'analytics_deals_performance_hist',
    'analytics_dim_calendar',
    'analytics_fact_advertiser',
    'analytics_fact_deal',
    'analytics_fact_employee',
    'analytics_fact_offer',
    'analytics_fact_publisher',
    'analytics_offer_channels',
    'analytics_offer_labels',
    'analytics_offers_performance_hist',
    'analytics_revenue_daily',
    'analytics_revenue_hist',
    'analytics_tradeshow_analysis',
    'analytics_trend_adjustments',
    'bd_team_upload_targets',
    'bulk_file_uploads_list',
    'chat_genie',
    'chat_genie_sql_statement',
    'chat_genie_visualization',
    'consumer_profile_email',
    'consumer_profile_md5',
    'daily_epc',
    'data_dictionary_apply',
    'data_dictionary_bulk_update',
    'data_dictionary_folders',
    'data_dictionary_pending_changes',
    'data_dictionary_publish_status',
    'data_dictionary_search',
    'data_dictionary_stats',
    'data_dictionary_tags',
    'data_dictionary_update',
    'email_list',
    'email_list_download_history',
    'email_list_files',
    'end_upload_universe_parts',
    'everflow_click_counts',
    'everflow_clicks',
    'everflow_clicks_conversions_summary',
    'everflow_conversion_counts',
    'everflow_conversions',
    'everflow_offer_history',
    'everflow_offers',
    'fast_id_match_rate',
    'fast_id_report',
    'file_upload_report_get',
    'file_uploads_list',
    'general_feature_values',
    'general_features',
    'hubspot_deals_pipeline_addition',
    'hubspot_tickets',
    'madrivo_trend',
    'offer_report_feature_values',
    'offer_report_features',
    'offer_report_general_feature_values',
    'offer_report_general_features',
    'offer_report_list',
    'offer_report_top_pairs',
    'offer_report_typeahead',
    'offer_report_values_feature_values',
    'offer_report_values_features',
    'offer_report_vertical_feature_values',
    'offer_report_vertical_features',
    'offer_studio_feature',
    'offer_studio_features',
    'offer_studio_insights',
    'offer_studio_offers',
    'offer_studio_overview',
    'offer_studio_segment_suggestions',
    'requested_email_list_download',
    'requested_email_list_status',
    'requested_segment_download',
    'requested_segment_download_status',
    'requested_signals_download',
    'requested_signals_status',
    'segment_analyze_conditions',
    'segment_column_metadata',
    'segment_column_options',
    'segment_column_resolve',
    'segment_column_search',
    'segment_column_typeahead',
    'segment_column_values',
    'segment_create',
    'segment_delete',
    'segment_download_request',
    'segment_draft',
    'segment_files',
    'segment_folders',
    'segment_get',
    'segment_list',
    'segment_preview_sql',
    'segment_run_count',
    'segment_tags',
    'segment_template_create',
    'segment_template_delete',
    'segment_template_get',
    'segment_template_list',
    'segment_template_update',
    'segment_update',
    'signal_download_history',
    'signal_files',
    'signal_request',
    'sql_warehouse_start',
    'start_upload_universe_parts',
    'upload_bulk_presign',
    'upload_bulk_s3',
    'upload_fast_id_hashes',
    'upload_fast_id_presign',
    'upload_fast_id_s3',
    'upload_status',
    'upload_universe_hashes',
    'upload_universe_part',
    'upload_universe_presign',
    'upload_universe_records',
    'upload_universe_s3',
    'vertical_feature_values',
    'vertical_features',
    'verticals',
  ];

  // Load organizations and users: FastCube admins see all; everyone else sees their orgs and themselves
  useEffect(() => {
    const loadData = async () => {
      if (!currentUser) return;

      try {
        const fastCubeAdmin = Boolean(currentUser?.is_admin && currentUser?.is_fastcube);
        setIsFastCubeAdmin(fastCubeAdmin);

        if (fastCubeAdmin) {
          const orgsRes = await api.get('/api/v2/admin/organizations');
          if (orgsRes.status === 200) {
            const orgs = orgsRes.data || [];
            const sortedOrgs = [...orgs].sort((a, b) => a.name.localeCompare(b.name));
            setOrganizations(sortedOrgs);
          }
          setOrganizationsLoading(false);

          const usersRes = await api.get('/api/v2/users/');
          if (usersRes.status === 200) {
            const usersData = usersRes.data || [];
            const sortedUsers = [...usersData].sort((a, b) => {
              const nameA = `${a.first_name || ''} ${a.last_name || ''}`.trim() || a.email;
              const nameB = `${b.first_name || ''} ${b.last_name || ''}`.trim() || b.email;
              return nameA.localeCompare(nameB);
            });
            setUsers(sortedUsers);
          }
          setSelectedUser('all');
          setUsersLoading(false);
          return;
        }

        const memberOrgs = await organizationsService.getOrganizations();
        const sortedOrgs = [...(memberOrgs || [])].sort((a, b) => a.name.localeCompare(b.name));
        setOrganizations(sortedOrgs);
        setOrganizationsLoading(false);

        const selfUser = {
          id: currentUser.id || currentUser.user_id,
          email: currentUser.email,
          first_name: currentUser.first_name,
          last_name: currentUser.last_name,
        };
        setUsers(selfUser.email ? [selfUser] : []);
        if (selfUser.email) {
          setSelectedUser(selfUser.email);
        }
        setUsersLoading(false);
      } catch (error) {
        console.error('Error loading organizations or users:', error);
        setIsFastCubeAdmin(false);
        setOrganizationsLoading(false);
        setUsersLoading(false);
      }
    };

    loadData();
  }, [currentUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    let fromTimestamp, toTimestamp;

    // Use date range if both dates are provided, otherwise use days dropdown
    if (fromDate && toDate) {
      // Parse dates as local time (YYYY-MM-DD format from date input)
      // Split the date string to get year, month, day
      const fromParts = fromDate.split('-');
      const toParts = toDate.split('-');
      
      if (fromParts.length !== 3 || toParts.length !== 3) {
        setErrorMessage('Please enter valid dates.');
        setShowErrorDialog(true);
        return;
      }
      
      // Create Date objects in local timezone
      // new Date(year, month, day, hour, minute, second, ms) creates date in local time
      const fromLocal = new Date(
        parseInt(fromParts[0], 10),
        parseInt(fromParts[1], 10) - 1, // Month is 0-indexed
        parseInt(fromParts[2], 10),
        0, 0, 0, 0 // Start of day in local time
      );
      
      const toLocal = new Date(
        parseInt(toParts[0], 10),
        parseInt(toParts[1], 10) - 1, // Month is 0-indexed
        parseInt(toParts[2], 10),
        23, 59, 59, 999 // End of day in local time
      );
      
      if (isNaN(fromLocal.getTime()) || isNaN(toLocal.getTime())) {
        setErrorMessage('Please enter valid dates.');
        setShowErrorDialog(true);
        return;
      }
      
      if (fromLocal > toLocal) {
        setErrorMessage('From date must be before or equal to To date.');
        setShowErrorDialog(true);
        return;
      }
      
      // These Date objects are in local time, toISOString() will convert to UTC
      fromTimestamp = fromLocal;
      toTimestamp = toLocal;
    } else {
      // Use days dropdown
      if (!days) {
        setErrorMessage('Please select a time period or enter a date range.');
        setShowErrorDialog(true);
        return;
      }

      const daysNum = parseInt(days, 10);
      if (isNaN(daysNum) || daysNum < 1 || daysNum > 365) {
        setErrorMessage('Number of days must be between 1 and 365.');
        setShowErrorDialog(true);
        return;
      }

      // Calculate timestamps from days (using local time)
      const now = new Date(); // Current time in local timezone
      toTimestamp = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999); // End of today in local time
      fromTimestamp = new Date(toTimestamp);
      fromTimestamp.setDate(fromTimestamp.getDate() - daysNum);
      fromTimestamp.setHours(0, 0, 0, 0); // Start of that day in local time
    }
    
    // Format as ISO 8601 strings (converts local time to UTC)
    const fromTimestampStr = fromTimestamp.toISOString();
    const toTimestampStr = toTimestamp.toISOString();

    setIsSubmitting(true);
    setRecords(null);
    setListOffset(0);
    setSortByField(DEFAULT_SORT_BY_FIELD);
    setSortOrder(DEFAULT_SORT_ORDER);
    setExpandedRows(new Set());

    try {
      // Build query parameters
      const params = new URLSearchParams();
      params.append('from_timestamp', fromTimestampStr);
      params.append('to_timestamp', toTimestampStr);
      
      if (selectedOrg && selectedOrg !== 'all') {
        params.append('org', selectedOrg);
      }

      if (isFastCubeAdmin && selectedUser && selectedUser !== 'all') {
        params.append('user', selectedUser);
      }
      
      // Add endpoint filter if not "all"
      if (selectedEndpoint && selectedEndpoint !== 'all') {
        params.append('endpoint', selectedEndpoint);
      }

      // Add execution_id filter if provided (only 0-9a-f- allowed)
      const executionIdTrimmed = executionIdFilter?.trim() || '';
      if (executionIdTrimmed) {
        params.append('execution_id', executionIdTrimmed);
      }

      const res = await api.get(`/api/v2/api_usage?${params.toString()}`);
      setRecords(res.data.records || []);
      toast.success(`Retrieved ${res.data.records?.length || 0} API usage records`);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to retrieve API usage records';
      setErrorMessage(errorMsg);
      setShowErrorDialog(true);
      toast.error('Failed to retrieve API usage records');
    } finally {
      setIsSubmitting(false);
    }
  };

  const sortedRecords = useMemo(() => {
    if (!records) return [];
    const copy = [...records];
    copy.sort((a, b) => compareUsageRecords(a, b, sortByField, sortOrder));
    return copy;
  }, [records, sortByField, sortOrder]);

  const pagedRecords = sortedRecords.slice(listOffset, listOffset + LIST_PAGE_SIZE);
  const totalRecords = records?.length ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalRecords / LIST_PAGE_SIZE) || 1);
  const currentPage = Math.floor(listOffset / LIST_PAGE_SIZE) + 1;

  useEffect(() => {
    if (listOffset > 0 && listOffset >= totalRecords) {
      setListOffset((prev) => Math.max(0, prev - LIST_PAGE_SIZE));
    }
  }, [totalRecords, listOffset]);

  const handleSortHeaderClick = (field) => {
    setListOffset(0);
    setExpandedRows(new Set());
    if (sortByField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortByField(field);
    setSortOrder(field === 'timestamp' || field === 'response_duration_ms' ? 'desc' : 'asc');
  };

  const renderSortableHeader = (label, field) => {
    const isActive = sortByField === field;
    const SortIcon = sortOrder === 'asc' ? ChevronUp : ChevronDown;
    return (
      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
        <button
          type="button"
          onClick={() => handleSortHeaderClick(field)}
          className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-gray-700"
          aria-label={`Sort by ${label}${isActive ? `, currently ${sortOrder}` : ''}`}
        >
          {label}
          {isActive ? <SortIcon size={14} aria-hidden="true" /> : null}
        </button>
      </th>
    );
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short'
      });
    } catch (e) {
      return timestamp;
    }
  };

  const toggleRow = (index) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRows(newExpanded);
  };

  const escapeCsvField = (value) => {
    if (value == null || value === '') return '""';
    const s = String(value);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return `"${s}"`;
  };

  const handleDownloadCsv = () => {
    if (!records || records.length === 0) return;
    const includeUser = isFastCubeAdmin;
    const headers = [
      'Timestamp',
      'Org',
      ...(includeUser ? ['User'] : []),
      'API Endpoint',
      'Response (ms)',
      'Full URL',
      'Execution ID',
      'Num Rows',
      'Num Bytes',
      'S3 File',
      'Databricks Duration (ms)',
      'Request content',
    ];
    const rows = records.map((r) => [
      formatTimestamp(r.timestamp),
      r.org ?? '',
      ...(includeUser ? [r.username ?? ''] : []),
      r.short_api ?? '',
      r.response_duration_ms ?? '',
      r.url ?? '',
      r.execution_id ?? '',
      r.num_rows ?? '',
      r.num_bytes ?? '',
      r.s3_file ?? '',
      r.databricks_duration_ms ?? '',
      r.request_content ?? '',
    ]);
    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map(escapeCsvField).join(','))
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `api-usage-${fromDate && toDate ? `${fromDate}-${toDate}` : `last-${days}-days`}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Download started');
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>API Usage</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <FunctionalSelect
                  label="Organization"
                  value={selectedOrg === 'all' ? 'all' : organizationsService.getSelectedOptionValue(organizations, selectedOrg, 'name')}
                  onValueChange={(value) => {
                    if (value === 'all') {
                      setSelectedOrg('all');
                    } else {
                      const parsed = organizationsService.parseOrganizationOptionValue(value);
                      setSelectedOrg(parsed ? parsed.name : value);
                    }
                  }}
                  placeholder="Select organization"
                  disabled={organizationsLoading}
                >
                  <SelectItem value="all">All</SelectItem>
                  {organizations.map((org) => (
                    <SelectItem key={org.id} value={organizationsService.orgToOptionValue(org)}>
                      {org.name}
                    </SelectItem>
                  ))}
                </FunctionalSelect>
              </div>

              <div className="space-y-2">
                <FunctionalSelect
                  label="User"
                  value={selectedUser}
                  onValueChange={setSelectedUser}
                  placeholder="Select user"
                  disabled={usersLoading || !isFastCubeAdmin}
                >
                  {isFastCubeAdmin && <SelectItem value="all">All</SelectItem>}
                  {users.map((user) => {
                    const displayName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email;
                    return (
                      <SelectItem key={user.id || user.email} value={user.email}>
                        {displayName} ({user.email})
                      </SelectItem>
                    );
                  })}
                </FunctionalSelect>
              </div>
            </div>

            <div className="space-y-2">
              <FunctionalSelect
                label="Time Period"
                value={days}
                onValueChange={setDays}
                placeholder="Select time period"
                className="w-full md:w-1/2"
              >
                <SelectItem value="1">Last day</SelectItem>
                <SelectItem value="2">Last 2 days</SelectItem>
                <SelectItem value="3">Last 3 days</SelectItem>
                <SelectItem value="7">Last week</SelectItem>
                <SelectItem value="14">Last 2 weeks</SelectItem>
                <SelectItem value="30">Last 1 month</SelectItem>
                <SelectItem value="90">Last 3 months</SelectItem>
                <SelectItem value="180">Last 6 months</SelectItem>
              </FunctionalSelect>
              {(fromDate && toDate) && (() => {
                const start = new Date();
                start.setDate(start.getDate() - parseInt(days, 10));
                const expectedFrom = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`;
                const expectedTo = getTodayDate();
                const isCustomRange = fromDate !== expectedFrom || toDate !== expectedTo;
                return isCustomRange ? <p className="text-xs text-gray-500">Using custom date range</p> : null;
              })()}
            </div>

            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2 w-full md:w-1/2">
                <Input
                  type="date"
                  id="fromDate"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  placeholder="From date"
                  className="w-full"
                />
                <Input
                  type="date"
                  id="toDate"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  placeholder="To date"
                  className="w-full"
                />
              </div>
              {(fromDate || toDate) && !(fromDate && toDate) && (
                <p className="text-xs text-amber-600">Please select both dates</p>
              )}
            </div>

            <div className="space-y-2">
              <FunctionalSelect
                label="Endpoint"
                value={selectedEndpoint}
                onValueChange={setSelectedEndpoint}
                placeholder="Select endpoint"
                className="w-full md:w-1/2"
              >
                <SelectItem value="all">All</SelectItem>
                {endpoints.map((endpoint) => (
                  <SelectItem key={endpoint} value={endpoint}>
                    {endpoint}
                  </SelectItem>
                ))}
              </FunctionalSelect>
            </div>

            <div className="space-y-2">
              <Input
                type="text"
                id="executionId"
                value={executionIdFilter}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^0-9a-fA-F-]/g, '');
                  setExecutionIdFilter(v);
                }}
                placeholder="Execution ID"
                className="font-mono text-sm"
                aria-label="Execution ID"
              />
              <p className="text-xs text-gray-500">Optional. Hex digits and hyphens only.</p>
            </div>

            <div className="flex items-center gap-2">
              <Button
                type="submit"
                variant="outline"
                disabled={isSubmitting}
                className="text-white"
              >
                {isSubmitting ? 'Loading...' : 'Submit'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {records !== null && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>API Usage Records ({records.length})</CardTitle>
              <Button
                type="button"
                variant="outline"
                disabled={records.length === 0}
                onClick={handleDownloadCsv}
                className="text-white"
                title={records.length === 0 ? 'No records to download' : 'Download results as CSV'}
              >
                <Download className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {records.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                No API usage records found for the selected time period.
              </p>
            ) : (
              <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {renderSortableHeader('Timestamp', 'timestamp')}
                      {renderSortableHeader('Org', 'org')}
                      {isFastCubeAdmin && renderSortableHeader('User', 'username')}
                      {renderSortableHeader('API Endpoint', 'short_api')}
                      {renderSortableHeader('Response (ms)', 'response_duration_ms')}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {pagedRecords.map((record, index) => {
                      const rowKey = listOffset + index;
                      const isExpanded = expandedRows.has(rowKey);
                      return (
                        <React.Fragment key={rowKey}>
                          <tr 
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={() => toggleRow(rowKey)}
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              <div className="flex items-center">
                                {isExpanded ? (
                                  <ChevronDown className="h-4 w-4 mr-2 text-gray-400" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 mr-2 text-gray-400" />
                                )}
                                {formatTimestamp(record.timestamp)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {record.org || '-'}
                            </td>
                            {isFastCubeAdmin && (
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {record.username || '-'}
                              </td>
                            )}
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {record.short_api}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {record.response_duration_ms}
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr className="bg-gray-50">
                              <td colSpan={isFastCubeAdmin ? 5 : 4} className="px-6 py-4">
                                <div className="text-sm space-y-1.5">
                                  <div>
                                    <span className="font-medium text-gray-700">Full URL: </span>
                                    <span className="text-gray-600 break-all select-all">{record.url || 'N/A'}</span>
                                  </div>
                                  {(record.request_content != null &&
                                    String(record.request_content).trim() !== '') && (
                                    <div>
                                      <span className="font-medium text-gray-700">Posted question: </span>
                                      <span className="text-gray-600 whitespace-pre-wrap break-words select-all">
                                        {record.request_content}
                                      </span>
                                    </div>
                                  )}
                                  {(record.execution_id != null && record.execution_id !== '') && (
                                    <div>
                                      <span className="font-medium text-gray-700">Execution ID: </span>
                                      <span className="text-gray-600 break-all select-all font-mono text-xs">{record.execution_id}</span>
                                    </div>
                                  )}
                                  {(record.s3_file != null && record.s3_file !== '') && (
                                    <div>
                                      <span className="font-medium text-gray-700">S3 File: </span>
                                      <span className="text-gray-600 break-all select-all font-mono text-xs">{record.s3_file}</span>
                                    </div>
                                  )}
                                  {(record.num_rows != null && record.num_rows !== '') && (
                                    <div>
                                      <span className="font-medium text-gray-700">Rows: </span>
                                      <span className="text-gray-600 select-all">{Number(record.num_rows).toLocaleString()}</span>
                                    </div>
                                  )}
                                  {(record.num_bytes != null && record.num_bytes !== '') && (
                                    <div>
                                      <span className="font-medium text-gray-700">Bytes: </span>
                                      <span className="text-gray-600 select-all">{formatBytes(record.num_bytes)}</span>
                                    </div>
                                  )}
                                  {(record.databricks_duration_ms != null && record.databricks_duration_ms !== '') && (
                                    <div>
                                      <span className="font-medium text-gray-700">Databricks Duration: </span>
                                      <span className="text-gray-600 select-all">{formatDurationMs(record.databricks_duration_ms)}</span>
                                    </div>
                                  )}
                                  {(record.clusters != null && String(record.clusters).trim() !== '') && (
                                    <div>
                                      <span className="font-medium text-gray-700">Clusters: </span>
                                      <span className="text-gray-600 break-all select-all font-mono text-xs">
                                        {String(record.clusters).replace(/,/g, ' ')}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between gap-3 pt-3 text-sm text-gray-600">
                <span>
                  {totalRecords.toLocaleString()} record
                  {totalRecords === 1 ? '' : 's'}
                  {` · page ${currentPage} of ${totalPages}`}
                </span>
                <ListPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  disabled={isSubmitting}
                  onPageChange={(page) => {
                    setListOffset((page - 1) * LIST_PAGE_SIZE);
                    setExpandedRows(new Set());
                  }}
                />
              </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      <ErrorDialog
        isOpen={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        message={errorMessage}
      />
    </div>
  );
};

export default APIUsage;
